/*
 * Renderer: dropdown  –  type="dropdown"
 *
 * A searchable, keyboard-accessible select with single- or multi-select mode.
 * Options are provided via setOptions([{ id, name, class? }]) and can be updated at any time.
 * Pass multiple lists instead (setOptions([[...], [...]])) to render them as separate
 * groups with a subtle divider between them.
 * Fires `input-change` (bubbles, composed) with detail { value } after each selection;
 * in multi-select mode value is an array of ids, in single mode a single id (or null).
 * Use getValue() / setValue() to read or set the current selection programmatically.
 *
 * Attributes:
 *   multiple     – enables multi-select: chips in the trigger, checkboxes in the list
 *   empty-option – prepends a blank "no selection" option (single mode only)
 *   empty-label  – label text for the empty option (default "–")
 *   placeholder  – shown when nothing is selected (default "Auswählen...")
 *   disabled     – disables all interaction
 */
export const DropdownRenderer = {
    renderDropdown() {
        const multiple = this.hasAttribute('multiple');
        this.clearShadow();

        const trigger = document.createElement('button');
        trigger.className = 'inputBtn inputTrigger' + (multiple ? ' inputTriggerMulti' : '');
        trigger.type      = 'button';
        trigger.disabled  = this.hasAttribute('disabled');
        trigger.setAttribute('aria-haspopup', 'listbox');
        trigger.setAttribute('aria-expanded', 'false');
        trigger.innerHTML = `
            <span class="inputTriggerLabel"></span>
            <i class="fa-solid fa-chevron-down inputChevron" aria-hidden="true"></i>
        `;

        const panel = document.createElement('div');
        panel.className = 'inputPanel dropdownPanel';
        panel.hidden    = true;
        panel.setAttribute('role', 'listbox');
        if (multiple) panel.setAttribute('aria-multiselectable', 'true');

        this.trigger = trigger;
        this.panel   = panel;

        this.buildOptions(panel, multiple);
        this.updateTriggerLabel(multiple);

        if (!trigger.disabled) {
            trigger.addEventListener('click', e => {
                if (e.target.closest?.('.inputChipRemove')) return;
                e.stopPropagation();
                this.togglePanel();
            });
            trigger.addEventListener('keydown', e => {
                switch (e.key) {
                    case 'Enter':
                    case ' ':
                        if (e.target.closest?.('.inputChipRemove')) return;
                        e.preventDefault();
                        this.togglePanel();
                        break;
                    case 'Escape':
                        this.closePanel();
                        break;
                    case 'ArrowDown':
                        e.preventDefault();
                        if (!this.isOpen) this.openPanel();
                        else focusOption(panel, null, 1);
                        break;
                    case 'ArrowUp':
                        e.preventDefault();
                        if (!this.isOpen) this.openPanel();
                        else focusOption(panel, null, -1);
                        break;
                }
            });
        }

        this.shadowRoot.appendChild(trigger);
        this.shadowRoot.appendChild(panel);
        this.attachOutsideHandler();
    },

    buildOptions(panel, multiple) {
        panel.innerHTML = '';

        const searchBar   = document.createElement('div');
        searchBar.className = 'inputSearchBar';
        const searchInput = document.createElement('input');
        searchInput.type         = 'text';
        searchInput.className    = 'inputSearchInput';
        searchInput.placeholder  = 'Suchen…';
        searchInput.autocomplete = 'off';

        searchInput.addEventListener('input', () => {
            const query = searchInput.value.trim().toLowerCase();
            panel.querySelectorAll('.inputOption').forEach(item => {
                const text = item.querySelector('span:last-of-type')?.textContent || item.textContent;
                item.style.display = text.toLowerCase().includes(query) ? '' : 'none';
            });
            const children = [...panel.querySelectorAll('.dropdownOptions > *')];
            children.forEach((element, index) => {
                if (!element.classList.contains('inputOptionDivider')) return;
                const hasVisibleBefore = children.slice(0, index).some(o => !o.classList.contains('inputOptionDivider') && o.style.display !== 'none');
                const hasVisibleAfter  = children.slice(index + 1).some(o => !o.classList.contains('inputOptionDivider') && o.style.display !== 'none');
                element.style.display = (hasVisibleBefore && hasVisibleAfter) ? '' : 'none';
            });
        });
        searchInput.addEventListener('click', e => e.stopPropagation());
        searchInput.addEventListener('keydown', e => {
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                e.stopPropagation();
                focusOption(panel, null, 1);
            } else if (e.key === 'Escape') {
                e.stopPropagation();
                this.closePanel();
                this.trigger?.focus();
            }
        });

        searchBar.appendChild(searchInput);
        panel.appendChild(searchBar);

        const optionsContainer = document.createElement('div');
        optionsContainer.className = 'dropdownOptions';

        if (!multiple && this.hasAttribute('empty-option')) {
            const emptyLabel = this.getAttribute('empty-label') || '–';
            const emptyItem  = document.createElement('div');
            emptyItem.className     = 'inputOption inputOptionEmpty';
            emptyItem.setAttribute('role', 'option');
            emptyItem.setAttribute('tabindex', '-1');
            emptyItem.dataset.value = '';
            emptyItem.textContent   = emptyLabel;

            const sel = this.value == null || this.value === '';
            if (sel) { emptyItem.classList.add('selected'); emptyItem.setAttribute('aria-selected', 'true'); }

            if (!this.hasAttribute('disabled')) {
                const emptyOpt = { id: '', name: emptyLabel };
                emptyItem.addEventListener('click', e => { e.stopPropagation(); this.selectOption(emptyOpt, multiple); });
                emptyItem.addEventListener('keydown', e => optionKeydown(e, panel, emptyItem,
                    () => this.selectOption(emptyOpt, multiple),
                    () => { this.closePanel(); this.trigger?.focus(); }
                ));
            } else {
                emptyItem.setAttribute('aria-disabled', 'true');
                emptyItem.classList.add('disabled');
            }
            optionsContainer.appendChild(emptyItem);
        }

        this.options.forEach(opt => {
            if (opt.divider) {
                optionsContainer.appendChild(document.createElement('div')).className = 'inputOptionDivider';
                return;
            }

            const item = document.createElement('div');
            item.className = 'inputOption';
            item.setAttribute('role', 'option');
            item.setAttribute('tabindex', '-1');
            item.dataset.value = opt.id;

            const sel = multiple
                ? (Array.isArray(this.value) && this.value.map(String).includes(String(opt.id)))
                : String(this.value) === String(opt.id);

            if (sel) { item.classList.add('selected'); item.setAttribute('aria-selected', 'true'); }

            if (opt.class) opt.class.trim().split(/\s+/).forEach(c => c && item.classList.add(c));

            if (multiple) {
                item.classList.add('inputOptionMulti');

                const checkSpan = document.createElement('span');
                checkSpan.className = `optionCheck${sel ? ' checked' : ''}`;
                if (sel) {
                    const checkIcon = document.createElement('i');
                    checkIcon.className = 'fa-solid fa-check';
                    checkSpan.appendChild(checkIcon);
                }

                const nameSpan = document.createElement('span');
                nameSpan.textContent = opt.name;

                item.appendChild(checkSpan);
                item.appendChild(nameSpan);
            } else {
                item.textContent = opt.name;
            }

            if (!this.hasAttribute('disabled')) {
                item.addEventListener('click', e => { e.stopPropagation(); this.selectOption(opt, multiple); });
                item.addEventListener('keydown', e => optionKeydown(e, panel, item,
                    () => this.selectOption(opt, multiple),
                    () => { this.closePanel(); this.trigger?.focus(); }
                ));
            } else {
                item.setAttribute('aria-disabled', 'true');
                item.classList.add('disabled');
            }

            optionsContainer.appendChild(item);
        });

        panel.appendChild(optionsContainer);
    },

    selectOption(opt, multiple) {
        if (multiple) {
            if (!Array.isArray(this.value)) this.value = [];
            const idx = this.value.map(String).indexOf(String(opt.id));
            if (idx === -1) this.value.push(opt.id);
            else            this.value.splice(idx, 1);
            this.updateTriggerLabel(multiple);
            const item = this.panel?.querySelector(`.inputOption[data-value="${CSS.escape(String(opt.id))}"]`);
            if (item) {
                const isSelected = this.value.map(String).includes(String(opt.id));
                item.classList.toggle('selected', isSelected);
                item.setAttribute('aria-selected', String(isSelected));
                const check = item.querySelector('.optionCheck');
                if (check) {
                    check.className = `optionCheck${isSelected ? ' checked' : ''}`;
                    check.innerHTML = isSelected ? '<i class="fa-solid fa-check"></i>' : '';
                }
            }
        } else {
            this.value = opt.id === '' ? null : opt.id;
            this.updateTriggerLabel(multiple);
            if (this.panel) this.buildOptions(this.panel, multiple);
            this.closePanel();
            this.trigger?.focus();
        }
        this.dispatchEvent(new CustomEvent('input-change', {
            bubbles: true, composed: true,
            detail: { value: this.value },
        }));
    },

    updateTriggerLabel(multiple) {
        const label = this.trigger?.querySelector('.inputTriggerLabel');
        if (!label) return;
        const placeholder = this.getAttribute('placeholder') || 'Auswählen...';

        let isPlaceholder = false;
        if (multiple) {
            label.innerHTML = '';
            const selectedOptions = Array.isArray(this.value)
                ? this.value.map(v => this.options.find(o => String(o.id) === String(v))).filter(Boolean)
                : [];
            if (selectedOptions.length > 0) {
                selectedOptions.forEach(opt => {
                    const chip = document.createElement('span');
                    chip.className = 'inputChip';

                    const nameSpan = document.createElement('span');
                    nameSpan.textContent = opt.name;
                    chip.appendChild(nameSpan);

                    if (!this.hasAttribute('disabled')) {
                        const removeIcon = document.createElement('i');
                        removeIcon.className = 'inputChipRemove fa-solid fa-xmark';
                        removeIcon.title     = `${opt.name} entfernen`;
                        removeIcon.addEventListener('click', e => {
                            e.stopPropagation();
                            e.preventDefault();
                            this.selectOption(opt, true);
                        });
                        chip.appendChild(removeIcon);
                    }
                    label.appendChild(chip);
                });
            } else {
                label.textContent = placeholder;
                isPlaceholder = true;
            }
        } else if (this.value != null && this.value !== '') {
            const found = this.options.find(o => String(o.id) === String(this.value));
            label.textContent = found?.name || placeholder;
            isPlaceholder = !found;
        } else {
            label.textContent = placeholder;
            isPlaceholder = true;
        }

        label.classList.toggle('is-placeholder', isPlaceholder);

        if (this.trigger) {
            this.trigger.setAttribute('aria-expanded', String(!!this.isOpen));
        }
    },
};

function optionKeydown(e, panel, item, onSelect, onEscape) {
    switch (e.key) {
        case 'Enter':
        case ' ':
            e.preventDefault();
            e.stopPropagation();
            onSelect();
            break;
        case 'Escape':
            e.stopPropagation();
            onEscape();
            break;
        case 'ArrowDown': {
            e.preventDefault();
            e.stopPropagation();
            focusOption(panel, item, 1);
            break;
        }
        case 'ArrowUp': {
            e.preventDefault();
            e.stopPropagation();
            const prev = focusOption(panel, item, -1);
            if (!prev) panel.querySelector('.inputSearchInput')?.focus();
            break;
        }
        case 'Home':
            e.preventDefault();
            e.stopPropagation();
            focusOption(panel, null, 1);
            break;
        case 'End':
            e.preventDefault();
            e.stopPropagation();
            focusOption(panel, null, -1);
            break;
    }
}

function focusOption(panel, current, delta) {
    const options = [...panel.querySelectorAll('.inputOption:not(.disabled)')]
        .filter(i => i.style.display !== 'none');
    if (!options.length) return null;
    if (current) {
        const idx  = options.indexOf(current);
        const next = options[idx + delta];
        if (next) { next.focus(); return next; }
        return null;
    }
    const target = delta > 0 ? options[0] : options[options.length - 1];
    if (target) { target.focus(); return target; }
    return null;
}
